// Shared standee types, constants and presets (docs/three-helpers-spec.md, 2a). Pure TypeScript:
// imported by Node tools with type stripping, so keep it erasable and free of three/React/DOM.

export const STANDEE_GENERATOR = 4; // bump to invalidate every bake
export const STANDEE_FILE_FORMAT = 'standee-bin-1';
/** The standee budget: the single source for the bake, build-distribution, the TS validator and the docs (the host repeats the numbers). */
export const STANDEE_BUDGET = { maxCount: 1024, maxFileBytes: 512 * 1024, maxTotalBytes: 16 * 1024 * 1024 } as const;
export const STANDEE_PRESETS = ['figure', 'token-flat', 'prop-thick', 'scenery'] as const;
export type StandeePreset = typeof STANDEE_PRESETS[number];
export type StandeeMounting = 'upright' | 'ground';
export const EDGE_RESOLUTIONS = [16, 32, 64, 128, 256, 512, 1024, 2048] as const;
export type EdgeResolution = typeof EDGE_RESOLUTIONS[number] | 'source';
export const DEFAULT_EDGE_RESOLUTION: EdgeResolution = 128;
export const DEFAULT_ALPHA_THRESHOLD = 128;
export function isEdgeResolution(v: unknown): v is EdgeResolution {
  return v === 'source' || (EDGE_RESOLUTIONS as readonly unknown[]).includes(v);
}
/** '"source"' or the number, as messages print it. */
export function formatEdgeResolution(e: EdgeResolution): string { return e === 'source' ? '"source"' : String(e); }
export const EDGE_RESOLUTION_MESSAGE = 'must be one of 16, 32, 64, 128, 256, 512, 1024, 2048 or "source"';

// There is no per-preset edge resolution: DEFAULT_EDGE_RESOLUTION applies to every preset.
export const PRESET_DEFAULTS: Record<StandeePreset, { thickness: number; mounting: StandeeMounting; edge: 'art' | string }> = {
  figure: { thickness: 0.06, mounting: 'upright', edge: 'art' },
  'token-flat': { thickness: 0.03, mounting: 'ground', edge: '#2b2723' },
  'prop-thick': { thickness: 0.25, mounting: 'upright', edge: 'art' },
  scenery: { thickness: 0.08, mounting: 'upright', edge: 'art' },
};

/** presentation.config.json field lists: the single source of truth for bake-standees.ts and the docs test. */
export const STANDEE_ENTRY_FIELDS = { required: ['id', 'front'], optional: ['back', 'preset', 'thickness', 'alphaThreshold', 'edgeResolution', 'profile', 'height', 'mounting', 'facing', 'edge'] } as const;
export const STANDEE_DEFAULTS_FIELDS = { required: [], optional: ['edgeResolution', 'height'] } as const;
export const LIMITS = {
  maxPixels: 4_194_304, // source image width*height
  maxSourceVertices: 20_000, // planar vertices
  maxBoundaryLoops: 256,
  maxOutputVertices: 65_535, // front + back + side
  maxFileBytes: STANDEE_BUDGET.maxFileBytes,
  weldTolerance: 1e-6, // local units
  diagonalChamferPixels: 0.05,
  positionQuantization: 1e6, // pixel-space exact weld key
  areaEpsilonScale: 0.25, // degenerate area <= weld^2 * scale
};
export type Vec2 = [number, number];
/** A planar triangle complex: positions, glTF top-origin UVs and triangle indices. */
export interface MeshSource { vertices: Vec2[]; uv: Vec2[]; triangles: number[] }
export interface MeshPart { positions: number[]; normals: number[]; uvs: number[]; indices: number[] } // flat xyz / uv arrays
export interface StandeeMeshData {
  front: MeshPart; back: MeshPart; side: MeshPart;
  boundaryLoops: number; boundaryEdges: number; // describe the caps
  sideLoopCount: number; sideEdges: number; // the loops and edges the side walls follow
  wallVertices: number; // vertices of the side-wall outlines (the cap boundary edges when no wall loops are given)
  bounds: { min: [number, number, number]; max: [number, number, number] };
  loops: Vec2[][]; // boundary loops in source units, for previews
}
export type DiagnosticCode = 'invalid-settings' | 'non-finite-input' | 'uv-mismatch' | 'index-range' | 'degenerate-triangle'
  | 'non-manifold-edge' | 'open-boundary' | 'topology-limit' | 'empty-source';
export interface Diagnostic { code: DiagnosticCode; message: string }
export interface BuildSettings {
  thickness: number; // source units, finite, > 0
  profile: 'flat';
  weldTolerance: number; // > 0
  maxSourceVertices: number; // >= 3
  maxBoundaryLoops: number; // >= 1
  maxOutputVertices: number; // >= 8
  backUv: 'mirror-front' | 'flip-u';
}
export interface StandeeImageRef { asset: string; sha256: string; width: number; height: number }
export interface StandeeMeta { // the JSON block of a standee file
  formatVersion: 'standee-bin-1'; generator: number; id: string; preset: StandeePreset;
  front: StandeeImageRef; back: StandeeImageRef | null;
  settings: { thickness: number; alphaThreshold: number; edgeResolution: EdgeResolution; profile: 'flat' };
  geometry: { thickness: number; sideLoops: 'wall' | 'cap' }; // thickness: solid depth in local units; sideLoops is 'cap' only after the wall-loop fallback
  artBounds: { min: [number, number]; max: [number, number] }; // bounding box of the source pixels at or above the threshold, local units (x, y)
  averageColor: string; // '#rrggbb', lowercase
  bounds: { min: [number, number, number]; max: [number, number, number] };
  stats: { sourceVertices: number; triangles: number; boundaryLoops: number; boundaryEdges: number; wallVertices: number; sideLoops: number; sideEdges: number; outputVertices: number; maskWidth: number; maskHeight: number };
  key: string; // 64 lowercase hex, see bake.ts
}
